import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, FileUp, PackageCheck, Search, Truck } from "lucide-react";
import { api } from "../lib/api";
import type { Order, OrderDetail } from "../lib/types";
import { dateTime, money, platformLabel, statusLabel } from "../lib/format";
import { Modal } from "../components/Modal";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

const sampleCsv = `platform,store,order_no,sku,title,quantity,unit_price,buyer,country,currency,ordered_at
amazon,Amazon US Store,AMZ-20260923-900001,USB-C-100W-BLK,100W USB-C 快充充电器,1,29.90,John Miller,US,USD,2026-09-23T08:30:00Z`;

const statusTabs = [
  { value: "", label: "全部订单" },
  { value: "pending_payment", label: "待付款" },
  { value: "pending_shipment", label: "待发货" },
  { value: "shipped", label: "已发货" },
  { value: "delivered", label: "已送达" },
  { value: "refunded", label: "售后/退款" },
  { value: "cancelled", label: "已取消" }
];

export function OrdersPage() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detailId, setDetailId] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [csv, setCsv] = useState(sampleCsv);
  const [shipment, setShipment] = useState({ carrier: "", trackingNo: "", shippingMethod: "", warehouse: "" });
  const [note, setNote] = useState("");
  const [nextStatus, setNextStatus] = useState("pending_shipment");

  const query = useQuery({
    queryKey: ["order-center", platform, status, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (platform) params.set("platform", platform);
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      return api.get<{ orders: Order[] }>(`/api/order-center/orders?${params.toString()}`);
    }
  });
  const detail = useQuery({
    queryKey: ["order-center-detail", detailId],
    queryFn: () => api.get<OrderDetail>(`/api/order-center/orders/${detailId}`),
    enabled: Boolean(detailId)
  });
  const importOrders = useMutation({
    mutationFn: () => api.post<{ imported: number; skipped: number; errors: string[] }>("/api/orders/import", { csv }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order-center"] });
    }
  });
  const bulk = useMutation({
    mutationFn: (action: "mark_shipped" | "cancel") =>
      api.post("/api/order-center/orders/bulk", { ids: selectedIds, action }),
    onSuccess: async () => {
      setSelectedIds([]);
      await queryClient.invalidateQueries({ queryKey: ["order-center"] });
    }
  });
  const updateOrder = useMutation({
    mutationFn: () => api.patch(`/api/order-center/orders/${detailId}`, { status: nextStatus, note }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order-center"] });
      await queryClient.invalidateQueries({ queryKey: ["order-center-detail", detailId] });
    }
  });
  const createShipment = useMutation({
    mutationFn: () => api.post(`/api/order-center/orders/${detailId}/shipment`, shipment),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order-center"] });
      await queryClient.invalidateQueries({ queryKey: ["order-center-detail", detailId] });
    }
  });

  const orders = query.data?.orders ?? [];
  const selectedAll = orders.length > 0 && orders.every((order) => selectedIds.includes(order.id));
  const currentOrder = detail.data?.order;
  const itemCount = (order: Order) => order.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;
  const firstItem = (order: Order) => order.items?.[0];

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const order of orders) counts[order.status] = (counts[order.status] ?? 0) + 1;
    return counts;
  }, [orders]);

  function openDetail(order: Order) {
    setDetailId(order.id);
    setNextStatus(order.status);
    setNote(order.note ?? "");
    setShipment({
      carrier: order.carrier ?? "",
      trackingNo: order.tracking_no ?? "",
      shippingMethod: order.shipping_method ?? "",
      warehouse: order.warehouse ?? ""
    });
  }

  return (
    <div className="page-stack">
      <section className="order-status-bar panel">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={status === tab.value ? "order-status-tab active" : "order-status-tab"}
            onClick={() => setStatus(tab.value)}
          >
            <span>{t(tab.label)}</span>
            {tab.value ? <strong>{statusCounts[tab.value] ?? 0}</strong> : <strong>{orders.length}</strong>}
          </button>
        ))}
      </section>

      <div className="toolbar-row">
        <div className="filter-row">
          <label className="search-box compact">
            <Search size={15} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("订单号、买家或运单号")} />
          </label>
          <select value={platform} onChange={(event) => setPlatform(event.target.value)}>
            <option value="">{t("全部平台")}</option>
            <option value="amazon">Amazon</option>
            <option value="tiktok">TikTok Shop</option>
            <option value="shein">SHEIN</option>
          </select>
        </div>
        <div className="button-row">
          <button type="button" className="ghost-button" disabled={selectedIds.length === 0} onClick={() => bulk.mutate("mark_shipped")}>
            <PackageCheck size={16} />
            {t("批量标记发货")}
          </button>
          <button type="button" className="ghost-button" onClick={() => setImportOpen(true)}>
            <FileUp size={16} />
            {t("导入订单")}
          </button>
        </div>
      </div>

      <div className="panel table-panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedAll}
                    onChange={(event) => setSelectedIds(event.target.checked ? orders.map((order) => order.id) : [])}
                  />
                </th>
                <th>{t("订单")}</th>
                <th>{t("商品")}</th>
                <th>{t("买家")}</th>
                <th>{t("状态")}</th>
                <th>{t("金额")}</th>
                <th>{t("物流")}</th>
                <th>{t("下单时间")}</th>
                <th>{t("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(order.id)}
                      onChange={(event) =>
                        setSelectedIds((current) =>
                          event.target.checked ? [...current, order.id] : current.filter((id) => id !== order.id)
                        )
                      }
                    />
                  </td>
                  <td>
                    <div className="order-number-cell">
                      <span className={`platform-dot dot-${order.platform}`} />
                      <div>
                        <strong>{order.platform_order_no}</strong>
                        <small>{platformLabel(order.platform)} · {order.store_name}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="order-product-cell">
                      <strong>{firstItem(order)?.title ?? t("商品明细")}</strong>
                      <small>{t("共 {count} 件", { count: itemCount(order) })} · {firstItem(order)?.platform_sku ?? "-"}</small>
                    </div>
                  </td>
                  <td>{order.buyer_name}<br /><small>{order.country}</small></td>
                  <td><StatusBadge tone={statusTone(order.status)}>{t(statusLabel(order.status))}</StatusBadge></td>
                  <td>{money(order.total_amount, order.currency)}</td>
                  <td>
                    <div className="logistics-cell">
                      <span>{order.carrier || t("未选择物流")}</span>
                      <small>{order.tracking_no || "-"}</small>
                    </div>
                  </td>
                  <td>{dateTime(order.ordered_at)}</td>
                  <td>
                    <button type="button" className="ghost-button compact-button" onClick={() => openDetail(order)}>
                      <Eye size={15} />
                      {t("详情")}
                    </button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 ? <tr><td colSpan={9} className="empty-table">{t("暂无订单")}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={Boolean(detailId)} title={t("订单详情")} onClose={() => setDetailId("")}>
        {detail.isLoading ? <div className="empty-state">{t("加载中...")}</div> : null}
        {detail.isError ? <div className="form-error">{detail.error.message}</div> : null}
        {currentOrder ? (
          <div className="order-detail">
            <div className="order-detail-head">
              <div>
                <span className={`platform-dot dot-${currentOrder.platform}`} />
                <strong>{currentOrder.platform_order_no}</strong>
                <small>{currentOrder.store_name} · {dateTime(currentOrder.ordered_at)}</small>
              </div>
              <StatusBadge tone={statusTone(currentOrder.status)}>{t(statusLabel(currentOrder.status))}</StatusBadge>
            </div>

            <div className="order-detail-grid">
              <section>
                <h3>{t("商品明细")}</h3>
                {detail.data?.items.map((item) => (
                  <div className="order-detail-item" key={item.id}>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.platform_sku} · {t("数量")} {item.quantity}</span>
                    </div>
                    <strong>{money(item.total_amount, currentOrder.currency)}</strong>
                  </div>
                ))}
                <div className="order-total-row">
                  <span>{t("订单总额")}</span>
                  <strong>{money(currentOrder.total_amount, currentOrder.currency)}</strong>
                </div>
              </section>
              <section>
                <h3>{t("买家信息")}</h3>
                <div className="detail-line"><span>{t("买家")}</span><strong>{currentOrder.buyer_name}</strong></div>
                <div className="detail-line"><span>{t("国家")}</span><strong>{currentOrder.country}</strong></div>
                <div className="detail-line"><span>{t("付款状态")}</span><strong>{currentOrder.payment_status}</strong></div>
                <div className="detail-line"><span>{t("履约状态")}</span><strong>{currentOrder.fulfillment_status}</strong></div>
              </section>
            </div>

            <section className="order-action-section">
              <h3>{t("订单处理")}</h3>
              <div className="order-action-grid">
                <label className="field">
                  <span>{t("订单状态")}</span>
                  <select value={nextStatus} onChange={(event) => setNextStatus(event.target.value)}>
                    {statusTabs.filter((tab) => tab.value).map((tab) => (
                      <option key={tab.value} value={tab.value}>{t(tab.label)}</option>
                    ))}
                  </select>
                </label>
                <label className="field span-2">
                  <span>{t("订单备注")}</span>
                  <input value={note} onChange={(event) => setNote(event.target.value)} />
                </label>
                <button type="button" className="ghost-button" onClick={() => updateOrder.mutate()} disabled={updateOrder.isPending}>
                  {t("保存状态和备注")}
                </button>
              </div>
            </section>

            <section className="order-action-section">
              <h3><Truck size={17} /> {t("发货和物流")}</h3>
              <div className="order-action-grid">
                <label className="field"><span>{t("物流商")}</span><input value={shipment.carrier} onChange={(event) => setShipment({ ...shipment, carrier: event.target.value })} /></label>
                <label className="field"><span>{t("运单号")}</span><input value={shipment.trackingNo} onChange={(event) => setShipment({ ...shipment, trackingNo: event.target.value })} /></label>
                <label className="field"><span>{t("物流方式")}</span><input value={shipment.shippingMethod} onChange={(event) => setShipment({ ...shipment, shippingMethod: event.target.value })} /></label>
                <label className="field"><span>{t("发货仓")}</span><input value={shipment.warehouse} onChange={(event) => setShipment({ ...shipment, warehouse: event.target.value })} /></label>
                <button
                  type="button"
                  className="primary-button"
                  disabled={!shipment.carrier || !shipment.trackingNo || createShipment.isPending}
                  onClick={() => createShipment.mutate()}
                >
                  <PackageCheck size={16} />
                  {createShipment.isPending ? t("提交中...") : t("标记发货并保存物流")}
                </button>
              </div>
            </section>

            <section className="order-history">
              <h3>{t("操作记录")}</h3>
              {detail.data?.history.map((item) => (
                <div className="history-row" key={item.id}>
                  <span>{dateTime(item.created_at)}</span>
                  <strong>{t(statusLabel(item.to_status))}</strong>
                  <small>{item.message}</small>
                </div>
              ))}
            </section>
          </div>
        ) : null}
      </Modal>

      <Modal open={importOpen} title={t("导入订单 CSV")} onClose={() => setImportOpen(false)}>
        <div className="form-stack">
          <p className="muted">{t("支持列：platform、store、order_no、sku、title、quantity、unit_price、buyer、country、currency、ordered_at。")}</p>
          <textarea className="code-textarea" value={csv} onChange={(event) => setCsv(event.target.value)} rows={12} />
          {importOrders.data ? (
            <div className="form-success">
              {t("导入 {imported} 条，跳过 {skipped} 条，错误 {errors} 条。", {
                imported: importOrders.data.imported,
                skipped: importOrders.data.skipped,
                errors: importOrders.data.errors.length
              })}
            </div>
          ) : null}
          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={() => setImportOpen(false)}>{t("关闭窗口")}</button>
            <button type="button" className="primary-button" disabled={importOrders.isPending} onClick={() => importOrders.mutate()}>
              {importOrders.isPending ? t("导入中...") : t("开始导入")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
